const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://your-railway-backend-url.railway.app/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    // Log API URL in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('API Base URL configured:', this.baseURL);
    }
  }

  // Helper method to get auth headers
  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    // Only log in development mode to reduce console noise
    if (process.env.NODE_ENV === 'development' && token) {
      console.log("getAuthHeaders - token from localStorage:", `Length: ${token.length}`);
    }
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
        ...options.headers,
      },
      ...options,
    };

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`API Request: ${options.method || 'GET'} ${url}`);
      }
      
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        return response.json();
      }
      return response.text();

    } catch (error) {
      // Enhanced error logging
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error(`API request failed - Network error. URL: ${url}`, error);
        throw new Error(`Network error: Unable to reach API at ${url}. Please check your connection and API configuration.`);
      }
      console.error(`API request failed for ${url}:`, error);
      throw error;
    }
  }

  // HTTP method helpers
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async put(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // Jobs API
  async getJobs() {
    return this.request('/jobs/');
  }

  async getPublicJobs() {
    return this.get('/jobs/public');
  }

  async getJob(id) {
    return this.request(`/jobs/${id}`);
  }

  async getPublicJob(id) {
    return this.get(`/jobs/public/${id}`);
  }

  async createJob(jobData) {
    return this.post('/jobs/', jobData);
  }

  async updateJob(id, jobData) {
    return this.put(`/jobs/${id}`, jobData);
  }

  async deleteJob(id) {
    return this.delete(`/jobs/${id}`);
  }

  // Companies API
  async getCompanies() {
    return this.request('/companies/');
  }

  async getPublicCompanies(initiative = null) {
    const params = initiative ? `?initiative=${initiative}` : '';
    return this.get(`/companies/public${params}`);
  }

  async getCompany(id) {
    return this.request(`/companies/${id}`);
  }

  async getPublicCompany(id) {
    return this.get(`/companies/public/${id}`);
  }

  async createCompany(companyData) {
    return this.post('/companies/', companyData);
  }

  async updateCompany(id, companyData) {
    return this.put(`/companies/${id}`, companyData);
  }

  async deleteCompany(id) {
    return this.delete(`/companies/${id}`);
  }

  // Events API - Public endpoints
  async getEvents() {
    return this.get('/events/public');
  }

  async getEvent(id) {
    return this.get(`/events/public/${id}`);
  }

  // Events API - Admin endpoints (require authentication)
  async getAdminEvents() {
    return this.get('/events/');
  }

  async getAdminEvent(id) {
    return this.get(`/events/${id}`);
  }

  async createEvent(eventData) {
    return this.post('/events/', eventData);
  }

  async updateEvent(id, eventData) {
    return this.put(`/events/${id}`, eventData);
  }

  async deleteEvent(id) {
    return this.delete(`/events/${id}`);
  }

  async updateEventStatus(id, status) {
    return this.put(`/events/${id}/status`, { status });
  }

  // Auth API
  async login(email, password) {
    const response = await fetch(`${this.baseURL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      throw new Error('Login failed');
    }

    // Return the raw JSON data. Do NOT store the token here.
    // The AuthContext will get the proper ID token from Firebase itself.
    return response.json();
  }

  async logout() {
    try {
      await this.post('/auth/logout');
    } finally {
      localStorage.removeItem('authToken');
    }
  }

  async getCurrentUser() {
    return this.get('/auth/me');
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;

export const getPublicJobs = async () => {
    const response = await apiService.get('/jobs/public');
    return response;
};

export const getPublicJob = async (id) => {
    const response = await apiService.get(`/jobs/public/${id}`);
    return response;
};

// Authenticated Job endpoints
export const getJobs = async () => {
    const response = await apiService.get('/jobs/');
    return response;
};

export const getJob = async (id) => {
    const response = await apiService.get(`/jobs/${id}`);
    return response;
};

export const createJob = async (jobData) => {
    const response = await apiService.post('/jobs/', jobData);
    return response;
};

export const updateJob = async (id, jobData) => {
    const response = await apiService.put(`/jobs/${id}`, jobData);
    return response;
};

export const deleteJob = async (id) => {
    const response = await apiService.delete(`/jobs/${id}`);
    return response;
};

// Company endpoints
export const getCompanies = async () => {
    const response = await apiService.get('/companies/');
    return response;
}; 